import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, withModifiers as _withModifiers, withKeys as _withKeys, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode } from "vue"

const _hoisted_1 = ["aria-label", "aria-expanded", "aria-describedby"]
const _hoisted_2 = ["lang"]
const _hoisted_3 = {
  key: 0,
  class: "language-menu"
}
const _hoisted_4 = ["aria-label"]
const _hoisted_5 = ["aria-checked", "lang", "onClick"]
const _hoisted_6 = {
  key: 0,
  viewBox: "0 0 24 24",
  fill: "none",
  "aria-hidden": "true"
}
const _hoisted_7 = ["lang"]

import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { setLocale, supportedLocales } from '../i18n'
import type { AppLocale } from '../i18n'


export default /*@__PURE__*/_defineComponent({
  __name: 'LanguageSwitcher',
  setup(__props) {

const { t, locale } = useI18n({ useScope: 'global' })
const currentLabel = computed(() => supportedLocales.find(item => item.value === locale.value)?.label ?? locale.value)
const coverageNotes: Record<AppLocale, string> = {
  'zh-CN': '除简体中文外，其他语言尚未覆盖全部工具。目前查询类工具「音效播放器」、「特效播放器」和「BGM播放器」，以及「客户端 UI 动画编辑器」支持五种语言。',
  'zh-TW': '除簡體中文外，其他語言尚未涵蓋全部工具。目前查詢類工具「音效播放器」、「特效播放器」和「BGM播放器」，以及「用戶端 UI 動畫編輯器」支援五種語言。',
  'en-US': 'Languages other than Simplified Chinese do not yet cover all tools. Five-language support is currently available in the Sound Effect Player, Effect Player, and BGM Player lookup tools, as well as the Client UI Animation Editor.',
  'ja-JP': '簡体字中国語以外の言語には、まだすべてのツールが対応しているわけではありません。現在、「効果音プレイヤー」、「エフェクトプレイヤー」、「BGMプレーヤー」の検索ツールと「クライアント UI アニメーションエディター」は5言語に対応しています。',
  'ru-RU': 'Полная локализация всего сайта пока доступна только на упрощённом китайском. Пять языков поддерживаются в инструментах поиска «Проигрыватель звуковых эффектов», «Проигрыватель эффектов», «BGM-плеер», а также в «Редакторе анимаций клиентского UI».',
}
const coverageNote = computed(() => coverageNotes[locale.value as AppLocale] ?? coverageNotes['zh-CN'])
const root = ref<HTMLElement | null>(null)
const trigger = ref<HTMLButtonElement | null>(null)
const menu = ref<HTMLElement | null>(null)
const open = ref(false)

async function show(last = false) {
  open.value = true
  await nextTick()
  const options = menu.value?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]')
  const selected = supportedLocales.findIndex(item => item.value === locale.value)
  options?.[last ? supportedLocales.length - 1 : Math.max(0, selected)]?.focus()
}

function close(restoreFocus = false) {
  open.value = false
  if (restoreFocus) trigger.value?.focus()
}

function toggle() {
  if (open.value) close()
  else void show()
}

function select(value: AppLocale) {
  setLocale(value)
  close(true)
}

function onMenuKeydown(event: KeyboardEvent) {
  const options = Array.from(menu.value?.querySelectorAll<HTMLButtonElement>('[role="menuitemradio"]') ?? [])
  const index = options.indexOf(document.activeElement as HTMLButtonElement)
  let next: number
  switch (event.key) {
    case 'ArrowDown': next = (index + 1) % options.length; break
    case 'ArrowUp': next = (index - 1 + options.length) % options.length; break
    case 'Home': next = 0; break
    case 'End': next = options.length - 1; break
    case 'Tab':
      // Return to the trigger before the browser moves focus out of the menu.
      close(true)
      return
    default: return
  }
  event.preventDefault()
  options[next]?.focus()
}

function onFocusOut(event: FocusEvent) {
  if (!root.value?.contains(event.relatedTarget as Node | null)) close()
}

function onPointerDown(event: PointerEvent) {
  if (!root.value?.contains(event.target as Node)) close()
}

onMounted(() => document.addEventListener('pointerdown', onPointerDown))
onBeforeUnmount(() => document.removeEventListener('pointerdown', onPointerDown))

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    ref_key: "root",
    ref: root,
    class: "language-picker",
    onFocusout: onFocusOut,
    onKeydown: _cache[2] || (_cache[2] = _withKeys(_withModifiers(($event: any) => (close(true)), ["stop","prevent"]), ["esc"]))
  }, [
    _createElementVNode("button", {
      ref_key: "trigger",
      ref: trigger,
      type: "button",
      class: _normalizeClass(["language-trigger", { 'is-open': open.value }]),
      "aria-label": `${_unref(t)('app.language')}: ${currentLabel.value}`,
      "aria-haspopup": "menu",
      "aria-expanded": open.value,
      "aria-controls": "app-language-menu",
      "aria-describedby": open.value ? 'language-coverage-note' : undefined,
      onClick: toggle,
      onKeydown: [
        _cache[0] || (_cache[0] = _withKeys(_withModifiers(($event: any) => (show()), ["prevent"]), ["down"])),
        _cache[1] || (_cache[1] = _withKeys(_withModifiers(($event: any) => (show(true)), ["prevent"]), ["up"]))
      ]
    }, [
      _cache[3] || (_cache[3] = _createElementVNode("svg", {
        class: "language-globe",
        viewBox: "0 0 24 24",
        fill: "none",
        "aria-hidden": "true"
      }, [
        _createElementVNode("circle", {
          cx: "12",
          cy: "12",
          r: "9"
        }),
        _createElementVNode("ellipse", {
          cx: "12",
          cy: "12",
          rx: "4",
          ry: "9"
        }),
        _createElementVNode("path", { d: "M3 12h18" })
      ], -1)),
      _createElementVNode("span", { lang: _unref(locale) }, _toDisplayString(currentLabel.value), 9, _hoisted_2),
      _cache[4] || (_cache[4] = _createElementVNode("svg", {
        class: "language-chevron",
        viewBox: "0 0 24 24",
        fill: "none",
        "aria-hidden": "true"
      }, [
        _createElementVNode("path", { d: "m6 9 6 6 6-6" })
      ], -1))
    ], 42, _hoisted_1),
    _createVNode(_Transition, { name: "language-menu" }, {
      default: _withCtx(() => [
        (open.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createElementVNode("div", {
                id: "app-language-menu",
                ref_key: "menu",
                ref: menu,
                role: "menu",
                "aria-label": _unref(t)('app.language'),
                onKeydown: onMenuKeydown
              }, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(supportedLocales), (language) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: language.value,
                    type: "button",
                    class: _normalizeClass(["language-option", { 'is-selected': _unref(locale) === language.value }]),
                    role: "menuitemradio",
                    "aria-checked": _unref(locale) === language.value,
                    lang: language.value,
                    tabindex: "-1",
                    onClick: ($event: any) => (select(language.value))
                  }, [
                    _createElementVNode("span", null, _toDisplayString(language.label), 1),
                    (_unref(locale) === language.value)
                      ? (_openBlock(), _createElementBlock("svg", _hoisted_6, [...(_cache[5] || (_cache[5] = [
                          _createElementVNode("path", { d: "m5 12 4 4L19 6" }, null, -1)
                        ]))]))
                      : _createCommentVNode("", true)
                  ], 10, _hoisted_5))
                }), 128))
              ], 40, _hoisted_4),
              _createElementVNode("p", {
                id: "language-coverage-note",
                class: "language-coverage-note",
                lang: _unref(locale)
              }, _toDisplayString(coverageNote.value), 9, _hoisted_7)
            ]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 544))
}
}

})